import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ChevronLeftIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getTeachingHall } from '../../../api/ols/teaching-halls/get-teaching-hall'
import { updateTeachingHall } from '../../../api/ols/teaching-halls/update-teaching-hall'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import QuotationSection from '../../../components/pages/ols/quotation/quotation-section'
import { TeachingHallAssignmentHistory } from '../../../components/pages/teaching-halls/teaching-hall-assignment-history'
import { TeachingHallComment } from '../../../components/pages/teaching-halls/teaching-hall-comment'
import TeachingHallForm from '../../../components/pages/teaching-halls/teaching-hall-form'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import useTranslate from '../../../lib/hooks/use-translate'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapTeachingHallToInput } from './utils'

const TeachingHallUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { t } = useTranslate()
  const olId = user?.ol?.id
  const hasValidParams = !!id && !isNaN(+id) && !!olId

  const { data: teachingHall, isPending } = useQuery({
    queryKey: ['teaching-halls', id, olId],
    queryFn: () => getTeachingHall({ id: Number(id), olId: Number(olId) }),
    enabled: hasValidParams
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateTeachingHall,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['teaching-halls', id] })
      toast.success(t('teaching.hall.success.message'))
    }
  })

  const olPermissions = getOlPermissions({ role: user?.role, type: user.ol?.type })

  if (!id || isNaN(+id) || !olId)
    return <ItemNotFound title="Identifiant de salle de cours invalide" />

  if (isPending) return <LoadingScreen />
  if (!teachingHall) return <NotFoundPage />

  return (
    <div className="flex flex-col gap-4">
      <UserNavBar
        title={teachingHall.name}
        icon={ChevronLeftIcon}
        onIconClick={() => navigate(Paths.TEACHING_HALLS_LIST_PAGE, { replace: true })}
      />

      <Box>
        <TeachingHallForm
          defaultValues={mapTeachingHallToInput(teachingHall)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.TEACHING_HALLS_LIST_PAGE),
            onUpdate: (values) => mutateAsync({ id: teachingHall?.id, olId, ...values }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: teachingHall?.id, olId, ...values })
              navigate(Paths.TEACHING_HALL_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: teachingHall?.id, olId, ...values })
              navigate(Paths.TEACHING_HALLS_LIST_PAGE, { replace: true })
            },
            isEnglish: true
          })}
        />
      </Box>

      <Box className="flex flex-col gap-4">
        {olPermissions['ol-teaching-hall-quotation'] && (
          <QuotationSection mode="teachingHall" teachingHallId={teachingHall.id} />
        )}

        {olPermissions['ol-teaching-hall-assignment-history'] && (
          <TeachingHallAssignmentHistory olId={olId} teachingHallId={teachingHall.id} />
        )}

        <TeachingHallComment teachingHallId={teachingHall.id} />
      </Box>
    </div>
  )
}

export default TeachingHallUpdatePage
